// @ts-nocheck
import React, { useState, useCallback } from 'react';
import {
  Form,
  NumberInput,
  Button,
  Tile,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Stack,
  InlineNotification,
  Modal,
  TextInput,
  Select,
  SelectItem,
  Checkbox,
  Loading,
  Slider,
  FormGroup,
} from '@carbon/react';
import { Calculator, Share, Download, ChartLine, DocumentPdf } from '@carbon/icons-react';
import { ROIVisualization } from './ROIVisualization';
import type {
  ROICalculatorInputs,
  ROICalculatorResults,
  CalculatorLeadCapture,
} from '../../../../shared/interfaces/EducationalCalculators';

interface ROICalculatorProps {
  onLeadCapture?: (lead: CalculatorLeadCapture) => void;
  variant?: 'standard' | 'wizard' | 'interactive';
}

export const ROICalculator: React.FC<ROICalculatorProps> = ({ onLeadCapture, variant = 'standard' }) => {
  const [inputs, setInputs] = useState<ROICalculatorInputs>({
    numberOfStudents: 30,
    averageStudentGrade: 75,
    currentTutoringCost: 0,
    hoursPerWeek: 10,
    weeksPerYear: 36,
  });

  const [results, setResults] = useState<ROICalculatorResults | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [leadData, setLeadData] = useState<Partial<CalculatorLeadCapture>>({
    role: 'educator',
    interestedInDemo: false,
  });

  const calculateROI = useCallback(async () => {
    setIsCalculating(true);

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // ROI Calculation Logic
    const platformCostPerStudent = 29.99; // Monthly cost
    const totalInvestment = inputs.numberOfStudents * platformCostPerStudent * 12;

    // Expected improvements based on research data
    const baseImprovement = 15; // Base 15% improvement
    const hoursMultiplier = Math.min(inputs.hoursPerWeek / 5, 2); // More hours = better results, capped at 2x
    const expectedGradeImprovement = baseImprovement * hoursMultiplier;

    const costPerGradePoint = totalInvestment / (expectedGradeImprovement * inputs.numberOfStudents);

    // Traditional tutoring comparison (avg $50/hour)
    const traditionalCostPerHour = 50;
    const traditionalYearlyCost =
      inputs.numberOfStudents * inputs.hoursPerWeek * inputs.weeksPerYear * traditionalCostPerHour;
    const savings = traditionalYearlyCost - totalInvestment;
    const percentageSaved = (savings / traditionalYearlyCost) * 100;

    // Value calculations
    const yearlyValue = savings + expectedGradeImprovement * 1000; // Each grade point worth $1000 in future earnings
    const fiveYearValue = yearlyValue * 5 * 1.1; // 10% compound growth

    const calculatedResults: ROICalculatorResults = {
      totalInvestment,
      expectedGradeImprovement,
      costPerGradePoint,
      yearlyValue,
      fiveYearValue,
      comparisonToTraditionalTutoring: {
        savings: Math.max(0, savings),
        percentageSaved: Math.max(0, percentageSaved),
      },
    };

    setResults(calculatedResults);
    setIsCalculating(false);
  }, [inputs]);

  const handleInputChange = (field: keyof ROICalculatorInputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setResults(null); // Clear results when inputs change
  };

  const handleShare = async () => {
    if (!leadData.name || !leadData.email) {
      return;
    }

    if (onLeadCapture) {
      const fullLeadData: CalculatorLeadCapture = {
        name: leadData.name!,
        email: leadData.email!,
        role: leadData.role || 'educator',
        institution: leadData.institution,
        interestedInDemo: leadData.interestedInDemo || false,
        calculatorResults: {
          calculatorType: 'roi',
          inputs,
          results,
          timestamp: new Date(),
        },
      };

      await onLeadCapture(fullLeadData);
    }

    setShowShareModal(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const generatePDFReport = async (results: ROICalculatorResults) => {
    // Create a formatted report content
    const reportContent = {
      title: 'Educational ROI Analysis Report',
      generatedDate: new Date().toLocaleDateString(),
      inputs: inputs,
      results: results,
      institution: leadData.institution || 'Your Institution',
      recommendations: [
        `Based on ${inputs.numberOfStudents} students, you can save ${formatCurrency(results.comparisonToTraditionalTutoring.savings)} annually`,
        `Expected grade improvement of ${formatPercentage(results.expectedGradeImprovement)} across your student population`,
        `ROI payback period: ${((results.totalInvestment / results.yearlyValue) * 12).toFixed(1)} months`,
        `5-year projected value: ${formatCurrency(results.fiveYearValue)}`,
      ],
    };

    try {
      // Call API to generate PDF
      const response = await fetch('/api/v1/calculators/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportContent),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ROI_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to text download
      const textContent = JSON.stringify(reportContent, null, 2);
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ROI_Report_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  };

  return (
    <div className="roi-calculator">
      <Stack gap={6}>
        <Tile>
          <h2 className="calculator-title">
            <Calculator size={24} /> Educational ROI Calculator
          </h2>
          <p className="calculator-description">
            Calculate the return on investment for implementing our AI-powered educational platform
          </p>
        </Tile>

        <Form>
          <Stack gap={5}>
            <NumberInput
              id="numberOfStudents"
              label="Number of Students"
              helperText="Total students who will use the platform"
              min={1}
              max={10000}
              value={inputs.numberOfStudents}
              onChange={(e, { value }) => handleInputChange('numberOfStudents', value || 0)}
            />

            <NumberInput
              id="averageStudentGrade"
              label="Current Average Grade (%)"
              helperText="Current class average performance (0-100)"
              min={0}
              max={100}
              value={inputs.averageStudentGrade}
              onChange={(e, { value }) => handleInputChange('averageStudentGrade', value || 0)}
            />

            <NumberInput
              id="currentTutoringCost"
              label="Current Tutoring Spend (Optional)"
              helperText="Annual spending on tutoring or supplemental education"
              min={0}
              value={inputs.currentTutoringCost}
              onChange={(e, { value }) => handleInputChange('currentTutoringCost', value || 0)}
            />

            <NumberInput
              id="hoursPerWeek"
              label="Hours of Usage Per Week"
              helperText="Expected platform usage hours per student"
              min={1}
              max={40}
              value={inputs.hoursPerWeek}
              onChange={(e, { value }) => handleInputChange('hoursPerWeek', value || 0)}
            />

            <NumberInput
              id="weeksPerYear"
              label="Weeks Per Year"
              helperText="Number of weeks the platform will be used"
              min={1}
              max={52}
              value={inputs.weeksPerYear}
              onChange={(e, { value }) => handleInputChange('weeksPerYear', value || 0)}
            />

            <Button kind="primary" onClick={calculateROI} disabled={isCalculating} renderIcon={ChartLine}>
              {isCalculating ? 'Calculating...' : 'Calculate ROI'}
            </Button>
          </Stack>
        </Form>

        {isCalculating && <Loading description="Calculating your ROI..." />}

        {results && !isCalculating && (
          <ROIVisualization
            results={results}
            variant="detailed"
            onShare={() => setShowShareModal(true)}
            onExportPDF={() => {
              // Generate PDF Report
              generatePDFReport(results);
            }}
          />
        )}

        {/* Lead Capture Modal */}
        <Modal
          open={showShareModal}
          onRequestClose={() => setShowShareModal(false)}
          modalHeading="Share Your ROI Results"
          primaryButtonText="Share & Get Demo"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleShare}
          primaryButtonDisabled={!leadData.name || !leadData.email}
        >
          <Stack gap={4}>
            <TextInput
              id="lead-name"
              labelText="Name"
              placeholder="Enter your name"
              required
              value={leadData.name || ''}
              onChange={e => setLeadData(prev => ({ ...prev, name: e.target.value }))}
            />

            <TextInput
              id="lead-email"
              labelText="Email"
              placeholder="Enter your email"
              type="email"
              required
              value={leadData.email || ''}
              onChange={e => setLeadData(prev => ({ ...prev, email: e.target.value }))}
            />

            <Select
              id="lead-role"
              labelText="Your Role"
              value={leadData.role}
              onChange={e => setLeadData(prev => ({ ...prev, role: e.target.value as any }))}
            >
              <SelectItem value="educator" text="Educator" />
              <SelectItem value="administrator" text="Administrator" />
              <SelectItem value="parent" text="Parent" />
              <SelectItem value="student" text="Student" />
            </Select>

            <TextInput
              id="lead-institution"
              labelText="Institution (Optional)"
              placeholder="School or organization name"
              value={leadData.institution || ''}
              onChange={e => setLeadData(prev => ({ ...prev, institution: e.target.value }))}
            />

            <Checkbox
              id="lead-demo"
              labelText="I'm interested in a personalized demo"
              checked={leadData.interestedInDemo}
              onChange={(_, { checked }) => setLeadData(prev => ({ ...prev, interestedInDemo: checked }))}
            />
          </Stack>
        </Modal>
      </Stack>
    </div>
  );
};
